import { useCallback, useState } from "react";
import Table, {
  TableActionBar,
  TableCell,
  TableRow,
} from "@amzn/meridian/table";
import {
  ACCESS_DENIED_MESSAGE,
  ADD_FAIL_STATUS,
  ADD_SUCCESS_STATUS,
  CAPACITY_LABEL,
  DEFAULT_SORT_DIRECTION,
  DELETE_FAIL_STATUS,
  DELETE_SUCCESS_STATUS,
  DUPLICATE_PP_CAPACITY,
  ITEMS_PER_PAGE,
  PLEASE_WAIT_STATUS,
  PPCAPACITY_HEADERS,
  PROCESS_PATH_LABEL,
  SIZE_LABEL,
  SORT_CENTER_LABEL,
  UPDATE_FAIL_STATUS,
  UPDATE_SUCCESS_STATUS,
  USER_ACCESS_KEY,
} from "../../constants";
import Button from "@amzn/meridian/button";
import Pagination from "@amzn/meridian/pagination";
import Column from "@amzn/meridian/column";
import ReadOnlyPPCapacityRow from "./ReadOnlyPPCapacityRow";
import EditablePPCapacityRow from "./EditablePPCapacityRow";
import PPCapacityAddForm from "./PPCapacityAddForm";
import { IId, IProcessPathCapacity } from "../../common/api/api";
import AmpApi from "../../common/api/AmpApi";
import SubmitModal from "../../components/modal/SubmitModal";
import filterTokenIcon from "@amzn/meridian-tokens/base/icon/filter";
import Input from "@amzn/meridian/input";
import Loader from "@amzn/meridian/loader";
import Toaster from "@amzn/meridian/toaster";
import Alert from "@amzn/meridian/alert";
import {TableSortDirection} from "@amzn/meridian/table/table";

function PPCapacityTable({ selectedNode, ppCapacityList }) {
  //useState for the ppc list
  const [ppcList, setPpcList] = useState(ppCapacityList);
  //useState for filtering table
  const [searchTerm, setSearchTerm] = useState("");
  //For the modal to display message
  const [openModal, setOpenModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const onConfirmClose = useCallback(() => setOpenModal(false), []);
  //For the loading sign for api calls
  const [loading, setLoading] = useState(false);
  //For pagination
  const itemsPerPage = ITEMS_PER_PAGE;
  const numberOfPages = Math.ceil(ppCapacityList.length / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;
  //Pagination
  //For sorting table columns
  const [sortColumn, setSortColumn] = useState(PPCAPACITY_HEADERS.PROCESS_PATH)
  const [sortDirection, setSortDirection] = useState<TableSortDirection>(DEFAULT_SORT_DIRECTION);
  const onSort = useCallback(({sortColumn, sortDirection}) => {
    setSortDirection(sortDirection)
    setSortColumn(sortColumn)
  }, [])
  //For duplicate records error
  let dummyId = 0;
  const [errorMessage, setErrorMessage] = useState("");
  const [toasts, setToasts] = useState([]);
  const onCloseToast = useCallback(
    (id) => setToasts(toasts.filter((t) => t.id !== id)),
    [toasts]
  );
  const onOpenToast = useCallback(
    () => setToasts(toasts.concat({ id: `${++dummyId}` })),
    [toasts]
  );
  //For Add Form
  const [isShown, setIsShown] = useState(false);
  const displayAddForm = () => {
    setIsShown((current) => !current);
  };
  const initialState: IProcessPathCapacity = {
    id: "",
    process_path: "",
    size: "",
    capacity: 0,
  };
  const [addFormData, setAddFormData] = useState(initialState);

  /**
   * This function adds new Process Path Capacity record
   * @param addData The new Process Path Rate to be added.
   */
  function addFormHandler(addData) {
    let result = ppcList.filter(function (v, i) {
      return v.process_path == addData.process_path && v.size == addData.size;
    });
    if (result.length > 0) {
      setErrorMessage(DUPLICATE_PP_CAPACITY);
      onOpenToast();
    } else {
      const addList: IProcessPathCapacity[] = [];
      const finalList = [addData, ...addList];
      setLoading(true);
      //Making api call for add
      (async () => {
        try {
          await AmpApi.updatePPCapacity(selectedNode, finalList);
          setLoading(false);
          setOpenModal(true);
          setResponseMessage(ADD_SUCCESS_STATUS);
          const newFormData = { ...addData };
          setAddFormData(newFormData);
          setPpcList([addData, ...ppcList]);
        } catch (ex: any) {
          setLoading(false);
          if(localStorage.getItem(USER_ACCESS_KEY)=="Denied"){
            setErrorMessage(ACCESS_DENIED_MESSAGE);
            onOpenToast();
          }
          else {
            console.error(`Error updating ${selectedNode}`, ex);
            setOpenModal(true);
            setResponseMessage(ADD_FAIL_STATUS);
          }
        }
      })();
      //End of the api call for add
    }
  }

  //To edit data
  const [editPpcId, setEditPpcId] = useState(null);
  const [editFormData, setEditFormData] = useState(initialState);

  /**
   * This function Updates the process path capacity info
   * @param newFormData Object contains the updated data
   */
  function handleEditFormChange(newFormData) {
    let result = ppcList.filter(function (v, i) {
      return (
        v.process_path == newFormData.process_path && v.size == newFormData.size
      );
    });
    if (result.length > 0 && result[0].id != newFormData.id) {
      setErrorMessage(DUPLICATE_PP_CAPACITY);
      onOpenToast();
    } else {
      const updateList: IProcessPathCapacity[] = [];
      const finalList = [newFormData, ...updateList];
      setOpenModal(true);
      setResponseMessage(PLEASE_WAIT_STATUS);
      //Making api call for update
      (async () => {
        try {
          await AmpApi.updatePPCapacity(selectedNode, finalList);
          setResponseMessage(UPDATE_SUCCESS_STATUS);
          const newEditPpcList = [...ppcList];
          setEditFormData(newFormData);
          const index = newEditPpcList.findIndex((obj) => {
            return obj.id === newFormData.id;
          });
          newEditPpcList[index] = newFormData;
          setPpcList(newEditPpcList);
          setEditPpcId(null);
        } catch (ex: any) {
          if(localStorage.getItem(USER_ACCESS_KEY)=="Denied"){
            setOpenModal(false);
            setErrorMessage(ACCESS_DENIED_MESSAGE);
            onOpenToast();
          } else {
            console.error(`Error updating ${selectedNode}`, ex);
            setOpenModal(true);
            setResponseMessage(UPDATE_FAIL_STATUS);
          }
        }
      })();
      //End of the api call for update
    }
  }

  const handleEditClick = (ppc) => {
    setEditPpcId(ppc.id);
    const formValues: IProcessPathCapacity = {
      id: ppc.id,
      process_path: ppc.process_path,
      size: ppc.size,
      capacity: parseInt(ppc.capacity),
    };
    setEditFormData(formValues);
  };

  /**
   * To handle the cancel button
   */
  const cancelHandler = () => {
    setIsShown((current) => !current);
  };
  const handleCancelClick = () => {
    setEditPpcId(null);
  };

  /**
   * To handle the delete button
   * @param ppc The Process Path Capacity object to be deleted
   */
  const handleDeleteClick = (ppc) => {
    const idsList: IId[] = [];
    const finalIds: IId[] = [ppc, ...idsList];
    setOpenModal(true);
    setResponseMessage(PLEASE_WAIT_STATUS);
    (async () => {
      try {
        await AmpApi.deletePPCapacity(selectedNode, finalIds);
        setResponseMessage(DELETE_SUCCESS_STATUS);
        //Delete api call ends
        const newPpcList = [...ppcList];
        const index = newPpcList.findIndex((obj) => {
          return obj.id === ppc.id;
        });
        newPpcList.splice(index, 1);
        setPpcList(newPpcList);
      } catch (ex: any) {
        if(localStorage.getItem(USER_ACCESS_KEY)=="Denied"){
          setOpenModal(false);
          setErrorMessage(ACCESS_DENIED_MESSAGE);
          onOpenToast();
        } else {
          console.error(`Error deleting ${ppc}`, ex);
          setOpenModal(true);
          setResponseMessage(DELETE_FAIL_STATUS);
        }
      }
    })();
  };

  //To get a list of unique Process Path, Size
  let processPathsOnly = ppCapacityList.map((a) => a.process_path);
  let uniqueProcessPaths = [...new Set(processPathsOnly)];
  let sizes = ppCapacityList.map((a) => a.size);
  let uniqueSizes = [...new Set(sizes)];

  ppCapacityList.sort((a, b) => {
    if (a[sortColumn] < b[sortColumn]) {
      return sortDirection === "ascending" ? -1 : 1
    }
    if (a[sortColumn] > b[sortColumn]) {
      return sortDirection === "ascending" ? 1 : -1
    }
    return 0
  })

  return (
    <>
      {errorMessage && (
        <Toaster toasts={toasts} onCloseToast={onCloseToast}>
          {(toast) => (
            <Alert toast={true} onClose={toast.onClose} type="error">
              {errorMessage}
            </Alert>
          )}
        </Toaster>
      )}
      <SubmitModal
        open={openModal}
        responseMessage={responseMessage}
        onClose={onConfirmClose}
      />
      {isShown && (
        <Column>
          {loading ? (
            <Loader />
          ) : (
            <PPCapacityAddForm
              addFormHandler={addFormHandler}
              cancelHandler={cancelHandler}
              uniqueSizes={uniqueSizes}
              uniqueProcessPaths={uniqueProcessPaths}
              selectedNode={selectedNode}
            />
          )}
        </Column>
      )}
      <Column>
        <Table
            headerRows={1}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
            spacing="small"
        >
          <TableActionBar>
            <Button onClick={displayAddForm}>
              {isShown ? "Close" : "Add New"}
            </Button>
            <Input
              key="searchInput"
              type="text"
              value={searchTerm}
              placeholder="Search by Process Path Name"
              prefixIconTokens={filterTokenIcon}
              onChange={setSearchTerm}
            />
          </TableActionBar>
          <TableRow>
            <TableCell width={100}>{SORT_CENTER_LABEL}</TableCell>
            <TableCell sortColumn={PPCAPACITY_HEADERS.PROCESS_PATH} width={200}>{PROCESS_PATH_LABEL}</TableCell>
            <TableCell sortColumn={PPCAPACITY_HEADERS.SIZE} width={150}>{SIZE_LABEL}</TableCell>
            <TableCell sortColumn={PPCAPACITY_HEADERS.CAPACITY} width={120}>{CAPACITY_LABEL}</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </Table>
        {ppcList
          .filter((ppc) => {
            if (searchTerm == "") {
              return ppc;
            } else if (
              ppc.process_path.toLowerCase().includes(searchTerm.toLowerCase())
            ) {
              return ppc;
            }
          })
          .slice(firstVisibleIndex, lastVisibleIndex)
          .map((ppc) => (
            <div key={ppc.id}>
              {editPpcId === ppc.id ? (
                <EditablePPCapacityRow
                  ppc={ppc}
                  selectedNode={selectedNode}
                  handleEditFormChange={handleEditFormChange}
                  handleCancelClick={handleCancelClick}
                  uniqueSizes={uniqueSizes}
                  uniqueProcessPaths={uniqueProcessPaths}
                />
              ) : (
                <ReadOnlyPPCapacityRow
                  ppc={ppc}
                  selectedNode={selectedNode}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                />
              )}
            </div>
          ))}
        <Pagination
          showSkipArrows={true}
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Column>
    </>
  );
}

export default PPCapacityTable;
